import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import '../styles/pages/landing.css';

/**
 * Module card definitions per role.
 * Each entry specifies which roles can see it and the entry path per role type.
 */
const MODULE_CARDS = [
  {
    id: 'workshops',
    name: 'Workshops',
    description: 'Browse and enroll in AI-focused workshops and training sessions',
    paths: {
      admin: '/workshops',
      leader: '/workshops',
      ceo: '/workshops',
      tech_leader: '/workshops',
      user: '/workshops',
    },
    roles: ['admin', 'leader', 'ceo', 'tech_leader', 'user'],
    allUsers: true,
  },
  {
    id: 'assessment',
    name: 'AI Assessment',
    description: 'Take or view AI readiness assessments for your company',
    paths: { ceo: '/assessment', admin: '/admin/ai-assessment', leader: '/admin/ai-assessment' },
    roles: ['admin', 'leader', 'ceo'],
  },
  {
    id: 'initiatives',
    name: 'AI Initiatives',
    description: 'Track and manage AI initiatives across functional areas',
    paths: { ceo: '/tracker', tech_leader: '/tracker', admin: '/admin/initiatives', leader: '/admin/initiatives' },
    roles: ['admin', 'leader', 'ceo', 'tech_leader'],
  },
  {
    id: 'techstack',
    name: 'Tech Stack',
    description: 'Tell us which tech your company runs, or review every company’s answers',
    paths: { ceo: '/tech-stack', tech_leader: '/tech-stack', admin: '/admin/tech-survey', leader: '/admin/tech-survey' },
    roles: ['admin', 'leader', 'ceo', 'tech_leader'],
  },
  {
    id: 'scorecard',
    name: 'AI Scorecard',
    description: 'Portfolio-wide AI adoption analytics and KPIs',
    paths: { admin: '/admin/scorecard', leader: '/admin/scorecard' },
    roles: ['admin', 'leader'],
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Manage users, organizations, and question sets',
    paths: { admin: '/admin/users', leader: '/admin/users' },
    roles: ['admin', 'leader'],
  },
  {
    id: 'team',
    name: 'My Team',
    description: 'View and manage team members and their roles',
    paths: { ceo: '/team' },
    roles: ['ceo'],
  },
];

function getCardsForUser(role) {
  if (!role) return [];
  return MODULE_CARDS
    .filter((mod) => mod.allUsers || mod.roles.includes(role))
    .map((mod) => {
      const path = mod.paths[role] ?? mod.paths['user'] ?? Object.values(mod.paths)[0];
      return { ...mod, path };
    });
}

export default function LandingPage() {
  const { user, role, profile } = useAuth();

  const cards = getCardsForUser(role);

  // Standard user or no groups → redirect to /welcome
  if (cards.length === 0) {
    return <Navigate to="/welcome" replace />;
  }

  const displayName = profile?.name || user?.email || 'there';

  return (
    <div className="landing-page">
      <div className="landing-page__header">
        <h1 className="landing-page__title">Welcome, {displayName}</h1>
        <p className="landing-page__subtitle">Choose a module to get started</p>
      </div>

      <div className="landing-page__grid">
        {cards.map((card) => (
          <article key={card.id} className="landing-card">
            <h2 className="landing-card__name">{card.name}</h2>
            <p className="landing-card__description">{card.description}</p>
            <Link
              to={card.path}
              className="landing-card__link"
              aria-label={`Go to ${card.name}`}
            >
              Go
            </Link>
          </article>
        ))}
      </div>
    </div>
  );
}
